import React, { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { slugify } from '@/data/nav';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { ImageField } from '@/components/common/ImageUploadField';

const field = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none focus:border-[hsl(var(--gaze-gold))]';
const labelCls = 'text-xs font-medium text-muted-foreground';

const EMPTY = {
  title: '', slug: '', category: '', cover: '', excerpt: '', content: '',
  author: 'Gaze Editorial', publish_date: '', published: true,
  meta_title: '', meta_description: '', keywords: '', canonical_url: '',
  og_title: '', og_description: '', og_image: '',
};

const toPayload = (p) => ({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).map((k) => [k, p[k] ?? EMPTY[k]])) });

export default function AdminBlog() {
  const [posts, setPosts] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [tab, setTab] = useState('general');
  const [busy, setBusy] = useState(false);
  const [slugTouched, setSlugTouched] = useState(false);

  const load = () => api.get('/admin/blog').then((r) => setPosts(r.data)).catch(() => toast.error('Could not load posts'));
  useEffect(() => { load(); }, []);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));
  const openNew = () => { setEditing(null); setForm({ ...EMPTY, publish_date: new Date().toISOString().slice(0, 10) }); setTab('general'); setSlugTouched(false); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm(toPayload(p)); setTab('general'); setSlugTouched(true); setOpen(true); };
  const onTitle = (v) => update(slugTouched ? { title: v } : { title: v, slug: slugify(v) });

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/blog/${editing.id}`, form);
      else await api.post('/admin/blog', form);
      toast.success(editing ? 'Post updated' : 'Post created');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to save'); }
    finally { setBusy(false); }
  };

  const togglePublish = async (p) => {
    try {
      await api.put(`/admin/blog/${p.id}`, { ...toPayload(p), published: p.published === false });
      toast.success(p.published === false ? 'Post published' : 'Post unpublished');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try { await api.delete(`/admin/blog/${p.id}`); toast.success('Post deleted'); load(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Blog</h1>
          <p className="text-sm text-muted-foreground">Create and manage Journal posts with full SEO control.</p>
        </div>
        <button onClick={openNew} data-testid="admin-blog-add-btn" className="inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-4 py-2 text-sm font-semibold text-white"><Plus size={15} /> New Post</button>
      </div>

      <div className="mt-4 overflow-x-auto rounded-2xl border border-border bg-white">
        <table className="w-full text-sm" data-testid="admin-blog-table">
          <thead className="bg-[hsl(var(--gaze-ivory))] text-left text-xs uppercase text-muted-foreground">
            <tr><th className="p-3">Post</th><th className="p-3">Author</th><th className="p-3">Publish date</th><th className="p-3">Status</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {posts?.map((p) => (
              <tr key={p.id} className="border-t border-border" data-testid="admin-blog-row">
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    {p.cover ? <img src={p.cover} alt="" className="h-10 w-14 rounded-lg object-cover" /> : <div className="h-10 w-14 rounded-lg bg-[hsl(var(--gaze-ivory))]" />}
                    <div><p className="font-medium">{p.title}</p><p className="text-[11px] text-muted-foreground">/blog/{p.slug}</p></div>
                  </div>
                </td>
                <td className="p-3 text-muted-foreground">{p.author || '-'}</td>
                <td className="p-3 text-xs">{p.publish_date || (p.created_at || '').slice(0, 10)}</td>
                <td className="p-3">{p.published === false ? <span className="rounded-full bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">Draft</span> : <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-700">Published</span>}</td>
                <td className="p-3">
                  <div className="flex items-center justify-end gap-2">
                    {p.published !== false && <a href={`/blog/${p.slug}`} target="_blank" rel="noreferrer" className="rounded-full border border-border p-1.5 text-muted-foreground hover:text-foreground" title="View"><ExternalLink size={13} /></a>}
                    <button onClick={() => togglePublish(p)} data-testid="admin-blog-publish-btn" className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold ${p.published === false ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>{p.published === false ? <><Eye size={12} /> Publish</> : <><EyeOff size={12} /> Unpublish</>}</button>
                    <button onClick={() => openEdit(p)} data-testid="admin-blog-edit-btn" className="inline-flex items-center gap-1 rounded-full border border-border px-3 py-1 text-xs font-semibold"><Pencil size={12} /> Edit</button>
                    <button onClick={() => del(p)} data-testid="admin-blog-delete-btn" className="rounded-full bg-red-50 p-1.5 text-red-600"><Trash2 size={13} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {posts && posts.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">No posts yet. Write your first story.</p>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Post' : 'New Post'}</DialogTitle>
            <DialogDescription>Journal content with full SEO and Open Graph control.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            {['general', 'seo'].map((s) => (
              <button key={s} onClick={() => setTab(s)} className={`rounded-full px-4 py-1.5 text-xs font-semibold capitalize ${tab === s ? 'bg-[hsl(var(--primary))] text-white' : 'border border-border'}`} data-testid={`admin-blog-tab-${s}`}>{s === 'seo' ? 'SEO & Social' : s}</button>
            ))}
          </div>
          {tab === 'general' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={labelCls}>Title *</label><input value={form.title} onChange={(e) => onTitle(e.target.value)} className={field} data-testid="admin-blog-title" /></div>
              <div>
                <label className={labelCls}>Slug</label>
                <input value={form.slug} onChange={(e) => { setSlugTouched(true); update({ slug: slugify(e.target.value) }); }} className={field} data-testid="admin-blog-slug" />
                <p className="mt-1 text-[11px] text-muted-foreground">/blog/{form.slug || '...'}</p>
              </div>
              <div><label className={labelCls}>Category / Topic</label><input value={form.category} onChange={(e) => update({ category: e.target.value })} className={field} placeholder="styling, guides..." data-testid="admin-blog-category" /></div>
              <div><label className={labelCls}>Author</label><input value={form.author} onChange={(e) => update({ author: e.target.value })} className={field} data-testid="admin-blog-author" /></div>
              <div><label className={labelCls}>Publish date</label><input type="date" value={form.publish_date} onChange={(e) => update({ publish_date: e.target.value })} className={field} data-testid="admin-blog-date" /></div>
              <div className="sm:col-span-2"><ImageField label="Featured image" value={form.cover} onChange={(v) => update({ cover: v })} testid="admin-blog-cover" previewClass="max-w-[280px]" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Excerpt</label><textarea rows={2} value={form.excerpt} onChange={(e) => update({ excerpt: e.target.value })} className={field} placeholder="Short summary shown on the listing and in search results" data-testid="admin-blog-excerpt" /></div>
              <div className="sm:col-span-2">
                <label className={labelCls}>Content</label>
                <textarea rows={12} value={form.content} onChange={(e) => update({ content: e.target.value })} className={field} placeholder={'Write your story. Separate paragraphs with a blank line.\nBasic HTML is also supported (<h2>, <p>, <strong>, <img>...).'} data-testid="admin-blog-content" />
              </div>
              <label className="flex items-center gap-2 text-sm sm:col-span-2">
                <input type="checkbox" checked={form.published} onChange={(e) => update({ published: e.target.checked })} className="accent-[hsl(var(--primary))]" data-testid="admin-blog-published" />
                Published (visible on the storefront)
              </label>
            </div>
          )}
          {tab === 'seo' && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="sm:col-span-2"><label className={labelCls}>SEO title</label><input value={form.meta_title} onChange={(e) => update({ meta_title: e.target.value })} className={field} placeholder={`${form.title || 'Post title'} · Gaze Decor Journal`} data-testid="admin-blog-meta-title" /></div>
              <div className="sm:col-span-2"><label className={labelCls}>Meta description</label><textarea rows={2} value={form.meta_description} onChange={(e) => update({ meta_description: e.target.value })} className={field} placeholder="~150 characters shown in search results" data-testid="admin-blog-meta-desc" /></div>
              <div><label className={labelCls}>Keywords</label><input value={form.keywords} onChange={(e) => update({ keywords: e.target.value })} className={field} placeholder="comma, separated" data-testid="admin-blog-keywords" /></div>
              <div><label className={labelCls}>Canonical URL</label><input value={form.canonical_url} onChange={(e) => update({ canonical_url: e.target.value })} className={field} placeholder="https://..." data-testid="admin-blog-canonical" /></div>
              <div><label className={labelCls}>OG title</label><input value={form.og_title} onChange={(e) => update({ og_title: e.target.value })} className={field} data-testid="admin-blog-og-title" /></div>
              <div><label className={labelCls}>OG description</label><input value={form.og_description} onChange={(e) => update({ og_description: e.target.value })} className={field} data-testid="admin-blog-og-desc" /></div>
              <div className="sm:col-span-2"><ImageField label="OG / Social share image" value={form.og_image} onChange={(v) => update({ og_image: v })} testid="admin-blog-og-image" previewClass="max-w-[280px]" /></div>
            </div>
          )}
          <button onClick={save} disabled={busy} data-testid="admin-blog-save-btn" className="w-full rounded-full bg-[hsl(var(--primary))] py-2.5 text-sm font-semibold text-white disabled:opacity-60">{busy ? 'Saving...' : editing ? 'Save Changes' : 'Create Post'}</button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
